/**
 * Opens the chart settings popup (screenshots/72, 76–79; §2.12) for a Chart / Table / Number widget and feeds it real rows:
 * every change to the binding re-queries `/api/datasets/:id` (bindingToQuery) and the preview keeps the old chart until the
 * rows arrive (81 #4: "preview morphs 300 ms after data"). Drafts autosave to `/api/me/overview/drafts/:widgetId`; Add/Save
 * writes `config.settings` into the layout (which autosaves through `/api/me/overview`).
 *
 * Also owns the overview cross-filter's Esc (81 #9) and opens the popup when a Chart/Table/Number tile is added from the library.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../app/router";
import { ChartSettingsDialog } from "./charts/ChartSettingsDialog";
import { bindingToQuery, defaultSettings, type ChartSettings, type Group } from "./charts/chartSettings";
import { createChartSettingsStore } from "./charts/chartSettingsStore";
import { CHART_TYPES, fits, suggestType, type Shape } from "./charts/chartTypes";
import { useThemes } from "./charts/themeStore";
import { chartEditorStore, crossTargets, loadThemes, overviewCrossFilter, setOverviewFilter, themeStore, useChartEditor } from "./chartEditorStore";
import { normalizeItems, overviewLayout, overviewStore, useOverview, useOverviewLayout } from "./overviewStore";
import type { DatasetResult, LayoutItem } from "./types";

const EMPTY: Shape = { kind: "category", unit: "count", rows: [] };
const draftUrl = (id: string) => `/api/me/overview/drafts/${encodeURIComponent(id)}`;

export function ChartEditorHost() {
  const open = useChartEditor((s) => s.open);
  const announce = useChartEditor((s) => s.announce);
  const snapshot = useOverview((s) => s.snapshot);

  useEffect(() => { loadThemes(snapshot?.themes.saved, snapshot?.themes.teamDefault); }, [snapshot]);

  // A Chart / Table / Number tile added from the library (click or drop) opens the popup in "add" mode.
  useEffect(() => overviewStore.subscribe((s) => s.justAdded, (ids, prev) => {
    const id = ids.find((x) => !prev.includes(x));
    const it = id && overviewLayout.getState().layout.find((i) => i.id === id);
    if (it && ["chart", "table", "number"].includes(it.type) && !it.config.settings && !it.config.metric) chartEditorStore.getState().edit(it.id, "add");
  }), []);

  // Esc clears the cross-filter everywhere (when no popup is open).
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented && !chartEditorStore.getState().open && overviewCrossFilter.getState().filter) setOverviewFilter(null); };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, []);

  return (
    <>
      <span className="cs-sr" role="status" aria-live="polite">{announce}</span>
      {open && <Editor key={open.widgetId} widgetId={open.widgetId} mode={open.mode} />}
    </>
  );
}

function initialFor(item: LayoutItem): ChartSettings {
  if (item.config.settings) return item.config.settings;
  const s = defaultSettings(item.type === "table" ? "table" : item.type === "number" ? "number" : "bar");
  return { ...s, data: { ...s.data, mapping: { category: "recipient", value: { field: "amount", agg: "sum" }, tooltip: [] } }, labels: { ...s.labels, title: item.config.title } };
}

function Editor({ widgetId, mode }: { widgetId: string; mode: "add" | "edit" }) {
  const item = useOverviewLayout((s) => s.layout.find((i) => i.id === widgetId));
  const [initial, setInitial] = useState<ChartSettings | null>(null);

  // Start from an unsaved draft when there is one (the popup was closed mid-edit), else from the widget.
  useEffect(() => {
    if (!item) return;
    let done = false;
    const start = (s: ChartSettings) => { if (!done) { done = true; setInitial(s); } };
    fetch(draftUrl(widgetId), { credentials: "include" }).then((r) => (r.ok ? r.json() : null))
      .then((d: { settings: ChartSettings | null } | null) => start(d?.settings?.version === 1 ? d.settings : initialFor(item)))
      .catch(() => start(initialFor(item)));
    const t = setTimeout(() => start(initialFor(item)), 800);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item || !initial) return null;
  return <EditorBody item={item} mode={mode} initial={initial} />;
}

function EditorBody({ item, mode, initial }: { item: LayoutItem; mode: "add" | "edit"; initial: ChartSettings }) {
  const widgetId = item.id;
  const store = useMemo(() => createChartSettingsStore(initial, {
    saveDraft: async (s) => {
      const r = await fetch(draftUrl(widgetId), { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(s) });
      if (!r.ok) throw new Error(String(r.status));
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const group = useOverview((s) => s.group) as Group;
  const snapshot = useOverview((s) => s.snapshot);
  const items = useOverviewLayout((s) => s.layout);
  const themes = useThemes(themeStore, (s) => s.themes);
  const first = useOverview((s) => s.datasets[widgetId]);
  const [result, setResult] = useState<DatasetResult | null>(first && first.query === bindingToQuery(initial.data) ? first : null);
  const [loading, setLoading] = useState(false);
  const lastDataset = useRef(initial.data.datasetId);

  // Re-query whenever the binding changes; keep the old rows on screen until the new ones arrive.
  useEffect(() => store.subscribe((s) => s.settings.data, (data) => query(bindingToQuery(data)), { fireImmediately: !result }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const seq = useRef(0);
  function query(url: string) {
    const n = ++seq.current;
    setLoading(true);
    fetch(url, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d: DatasetResult | null) => {
      if (n !== seq.current) return;
      setLoading(false);
      if (!d) return;
      setResult(d);
      // A new dataset keeps the user's type if it still fits, otherwise the suggested one (keepOrSuggest).
      const s = store.getState().settings;
      if (s.data.datasetId !== lastDataset.current) {
        lastDataset.current = s.data.datasetId;
        if (!fits(s.type, d.shape)) { const t = suggestType(d.shape); store.getState().update((x) => ({ ...x, type: t }), `Type: ${CHART_TYPES[t].label}`); }
      }
    }).catch(() => { if (n === seq.current) setLoading(false); });
  }

  const vars = useMemo(() => {
    const out: Record<string, Record<string, string | number>> = {};
    for (const r of result?.rows ?? []) {
      const extra = Object.fromEntries(Object.entries(r).filter(([k, v]) => !["key", "label", "value"].includes(k) && v != null)) as Record<string, string | number>;
      if (Object.keys(extra).length) out[String(r.key)] = extra;
    }
    return out;
  }, [result]);

  const dropDraft = () => { void fetch(draftUrl(widgetId), { method: "DELETE", credentials: "include" }).catch(() => {}); };
  const close = () => {
    dropDraft();
    chartEditorStore.getState().close();
    // Cancelling an "add" takes the empty widget off again.
    if (mode === "add") overviewLayout.getState().apply((l) => l.filter((i) => i.id !== widgetId), [widgetId]);
  };
  const save = (s: ChartSettings, o: { forTeam: boolean }) => {
    const ov = overviewStore.getState();
    ov.setConfig(widgetId, { settings: s, title: s.labels.title ?? item.config.title });
    if (o.forTeam && snapshot?.viewer.canPublish) {
      const saved = overviewLayout.getState().layout.find((i) => i.id === widgetId)!;
      const team = normalizeItems(snapshot.layouts.team?.items);
      const next = team.some((i) => i.id === widgetId) ? team.map((i) => (i.id === widgetId ? saved : i)) : [...team, { ...saved, y: 400 }];
      void fetch("/api/me/overview", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ scope: "team", items: next, baseVersion: snapshot.layouts.team?.version ?? 0, touched: [widgetId] }) }).catch(() => {});
    }
    dropDraft();
    chartEditorStore.getState().close();
  };

  return (
    <div className="cs-backdrop">
      <ChartSettingsDialog store={store} shape={result?.shape ?? first?.shape ?? EMPTY} loading={loading} vars={vars} result={result ?? first ?? null}
        group={group} themes={themes} canSaveForTeam={!!snapshot?.viewer.canPublish} mode={mode}
        crossTargets={(field) => crossTargets(items, field, widgetId)}
        onSave={save} onClose={close} onOpenThemes={() => { chartEditorStore.getState().close(); navigate("/dashboard/overview/themes"); }} />
    </div>
  );
}
